import React from 'react';
import brain1 from '@/pages/study-guide/study-guide-review/img/brain-1.svg';
import brain2 from '@/pages/study-guide/study-guide-review/img/brain-2.svg';
import brain3 from '@/pages/study-guide/study-guide-review/img/brain-3.svg';
import brain4 from '@/pages/study-guide/study-guide-review/img/brain-4.svg';
import brain5 from '@/pages/study-guide/study-guide-review/img/brain-5.svg';
import { Badge } from '@/components/ui/badge';
import { FiZap } from 'react-icons/fi';
import { cn } from '@/lib/utils';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';

// Painel do streak com a metáfora do cérebro enchendo (vazio → cheio), resgatado do
// study-guide-review (gradiente azul→branco→amarelo, anel circular, badge de nível).
const STREAK_GOAL = 7; // dias para o cérebro "cheio" + anel 100%
const BRAINS = [null, brain1, brain2, brain3, brain4, brain5];
const RING_COLOR = { 1: '#EF4444', 2: '#F97316', 3: '#EAB308', 4: '#22C55E', 5: '#10B981' };
const LEVEL = {
  1: { variant: 'danger', label: 'Iniciante' },
  2: { variant: 'third', label: 'Aprendiz' },
  3: { variant: 'warning', label: 'Praticante' },
  4: { variant: 'success-light', label: 'Avançado' },
  5: { variant: 'success', label: 'Mestre' }
};

const levelFor = count => {
  if (count <= 0) return 1;
  if (count <= 2) return 2;
  if (count <= 4) return 3;
  if (count <= 6) return 4;
  return 5;
};

export function StreakPanel({ streak, walked = 0, mastered = 0, onContinue, continueLabel = 'Próxima missão', className }) {
  const count = streak?.['current-count'] ?? 0;
  const level = levelFor(count);
  const badge = LEVEL[level];
  const pct = Math.min(100, (count / STREAK_GOAL) * 100);

  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const dashoffset = circumference - (pct / 100) * circumference;
  const playful = usePlayfulStudent();

  return (
    <div
      className={cn('flex flex-col gap-5 p-7', playful ? 'rounded-[24px] border border-neutral-100 shadow-card' : 'rounded-2xl border border-solid', className)}
      style={
        playful
          ? { background: 'var(--color-bg-cream)' }
          : {
              background: 'linear-gradient(135deg, #DDFAFF 0%, #E8F4FF 50%, #FFF8E1 100%)',
              borderColor: 'rgba(93, 106, 134, 0.08)',
              boxShadow: '0 4px 12px rgba(93, 106, 134, 0.08)'
            }
      }
    >
      <div className="flex items-center gap-4">
        <div className="relative h-[112px] w-[112px] shrink-0">
          <svg
            viewBox="0 0 100 100"
            className="h-full w-full"
          >
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              strokeWidth="6"
              style={{ stroke: 'rgba(255, 255, 255, 0.6)' }}
            />
            <circle
              cx="50"
              cy="50"
              r={radius}
              fill="none"
              strokeWidth="6"
              strokeLinecap="round"
              style={{
                stroke: RING_COLOR[level],
                strokeDasharray: circumference,
                strokeDashoffset: dashoffset,
                transform: 'rotate(-90deg)',
                transformOrigin: '50% 50%',
                transition: 'stroke-dashoffset 0.6s ease-out',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))'
              }}
            />
          </svg>
          <div className="absolute left-1/2 top-1/2 flex h-[88px] w-[88px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.08)]">
            <img
              src={BRAINS[level]}
              alt="Cérebro do streak"
              style={{ width: 34 + level * 6, height: 'auto', transition: 'width 0.3s ease-out' }}
            />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-1.5">
          <Badge
            variant={badge.variant}
            size="xs"
            className="self-start"
          >
            {badge.label}
          </Badge>
          <span
            className="text-3xl font-extrabold leading-none"
            style={{ color: 'var(--color-primary-dark)' }}
          >
            🔥 {count}
          </span>
          <span
            className="text-[11px] font-semibold uppercase tracking-wide"
            style={{ color: 'var(--color-neutral)' }}
          >
            {count === 1 ? 'dia seguido' : 'dias seguidos'}
          </span>
        </div>
      </div>

      <div
        className="flex flex-col gap-3 rounded-[10px] p-3.5 backdrop-blur"
        style={{ background: 'rgba(255, 255, 255, 0.7)' }}
      >
        <StatBar
          value={walked}
          label="Percorrido"
          color="var(--color-third)"
        />
        {!playful && (
          <StatBar
            value={mastered}
            label="Dominado"
            color="var(--color-success)"
          />
        )}
      </div>

      {onContinue &&
        (playful ? (
          // Botão chanfrado igual aos cards das Conquistas: gradiente + lip colorido + brilho.
          <button
            type="button"
            onClick={onContinue}
            className="group/btn relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-full py-3.5 text-base font-extrabold text-white transition-transform duration-100 active:scale-[0.97]"
            style={{
              background: 'linear-gradient(180deg, color-mix(in srgb, var(--color-primary) 88%, #fff) 0%, var(--color-primary) 100%)',
              boxShadow: '0 4px 0 color-mix(in srgb, var(--color-primary) 62%, #000), 0 6px 14px rgba(0,0,0,0.18)'
            }}
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-1/2 rounded-t-full"
              style={{ background: 'linear-gradient(180deg, rgba(255,255,255,0.28) 0%, transparent 100%)' }}
            />
            <span className="relative flex items-center gap-2">
              <FiZap className="h-5 w-5" />
              {continueLabel}
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={onContinue}
            className="flex w-full items-center justify-center gap-2 rounded-full bg-primary px-6 py-3.5 text-base font-extrabold text-white shadow-[0_8px_20px_rgba(68,43,140,0.35)] transition hover:brightness-110 active:scale-[0.97]"
          >
            <FiZap className="h-5 w-5" />
            {continueLabel}
          </button>
        ))}
    </div>
  );
}

function StatBar({ value, label, color }) {
  return (
    <div>
      <div className="mb-1 flex items-baseline justify-between">
        <span
          className="text-[11px] font-semibold uppercase tracking-wide"
          style={{ color: 'var(--color-neutral)' }}
        >
          {label}
        </span>
        <span
          className="text-sm font-extrabold leading-none"
          style={{ color: 'var(--color-primary-dark)' }}
        >
          {value}%
        </span>
      </div>
      <div
        className="h-2 w-full overflow-hidden rounded-full"
        style={{ background: 'rgba(17, 11, 62, 0.08)' }}
      >
        <div
          className="h-full rounded-full transition-[width] duration-500 ease-out-strong"
          style={{ width: `${Math.max(value, 2)}%`, background: color }}
        />
      </div>
    </div>
  );
}
